import type { Change } from "./autosave.ts";
import type { Snapshot } from "./version.ts";
import type { Translate } from "./i18n";

/** Row names come from the compared version, never from another tenant or today's live catalogue. */
export function changeNames(changes: Change[], snapshot: Snapshot | undefined, t: Translate, idolName?: (id: string) => string): Change[] {
  const rivalNames = new Map((snapshot?.rivals ?? []).map((r) => [r.id ?? (r as unknown as { rivalId: string }).rivalId, r.name]));
  for (const c of changes) if (c.block === "rivals") {
    const id = /(?:^\[|,)rivalId=([^,\]]+)/.exec(c.path)?.[1];
    const name = c.row || (/\.name$/.test(c.path) ? c.to || c.from : undefined);
    if (id && typeof name === "string") { let decoded = id; try { decoded = decodeURIComponent(id); } catch { /* keep malformed historical ID readable */ } rivalNames.set(decoded, name); }
  }
  return changes.map((c) => {
    if (c.row) return c;
    const key = /^\[([^\]]+)\]/.exec(c.path)?.[1];
    if (!key) return c;
    const ids = Object.fromEntries(key.split(",").map((p) => {
      const [k, ...v] = p.split("=");
      let value = v.join("="); try { value = decodeURIComponent(value); } catch { /* old raw identity */ }
      return [k, value];
    }));
    if (c.block === "rivalCells" || c.block === "pettownRivalPrices") {
      const [rivalId, level, building, star] = ids.id ? String(ids.id).split("|") : [ids.rivalId, ids.level ?? ids.star, ids.buildingIdx, ids.starLevel];
      const name = rivalNames.get(rivalId);
      if (!name) return c;
      return { ...c, row: c.block === "rivalCells"
        ? t("auditChange.rivalAt", { name, level, building, star })
        : `${name} · ${level}★` };
    }
    if (!snapshot) return c;
    const rows = (snapshot as unknown as Record<string, unknown>)[c.block];
    if (!Array.isArray(rows)) return c;
    const row = /^\d+$/.test(key) ? rows[Number(key)] : rows.find((r) => r && typeof r === "object" && Object.entries(ids).every(([k, v]) => {
      const field = c.block === "levels" && k === "level" ? "id" : k;
      return ["buildingIdx", "starLevel", "buildingSet"].includes(k) || String(r[field]) === v;
    }));
    if (!row) return c;
    const text = (v: unknown): string => {
      if (typeof v === "string") return v;
      if (v && typeof v === "object") {
        const o = v as Record<string, unknown>;
        return [o["zh-CN"], o.en, ...Object.values(o)].find((x): x is string => typeof x === "string" && !!x.trim()) ?? "";
      }
      return "";
    };
    let name = text(row.name) || text(row.nameI18n) || text(row.titleI18n) || text(row.label);
    const ownerName = (id: string) => { const name = idolName?.(id); return name && name !== id ? `${name} (${id})` : id; };
    if (!name && c.block === "stageImages") {
      const stage = snapshot.stages.find((s) => s.stageId === row.stageId);
      name = t("auditChange.imageAt", { owner: ownerName(stage?.idolId ?? row.stageId), level: stage?.levelCode ?? "—", slot: row.slot });
    }
    if (!name && c.block === "stages") name = t("auditChange.stageAt", { owner: ownerName(row.idolId), level: row.levelCode });
    return name ? { ...c, row: name } : c;
  });
}

/** Historical rewards resolve references against their own version's catalogue. */
export function referenceName(snapshot: Snapshot | undefined, kind: string, ref: string, locale: string): string | undefined {
  const pick = (value: Record<string, string> | undefined, fallback: string) => value?.[locale] || value?.en || (value && Object.values(value).find(Boolean)) || fallback;
  if (!snapshot) return undefined;
  if (kind === "frame") {
    const f = snapshot.frames?.find((x) => x.frameId === ref);
    return f ? pick(f.nameI18n, f.name) : undefined;
  }
  if (kind !== "itemCard" && kind !== "idolCard") return undefined;
  for (const pack of kind === "itemCard" ? snapshot.itemPacks ?? [] : snapshot.idolPacks ?? []) {
    const pn = pick(pack.i18n ?? (pack as unknown as { nameI18n?: Record<string, string> }).nameI18n, pack.name);
    if ([pack.id, pack.code, pack.name].includes(ref)) return pn;
    for (const card of pack.cards) if (ref === card.code || ref === `${pack.name} · ${card.name}`) {
      return `${pn} · ${pick(card.i18n ?? (card as unknown as { nameI18n?: Record<string, string> }).nameI18n, card.name)}`;
    }
  }
  return undefined;
}
